/**
 * useDispatchSocket Hook
 *
 * Manages Socket.IO connection to the /dispatch namespace for real-time
 * dashboard updates. Provides connection status and event handlers for
 * patrol events, checkpoint completions, location updates, and alerts.
 *
 * Issue 5.1: Create useDispatchSocket hook
 *
 * Features:
 * - Connects to /dispatch namespace on mount
 * - Auto-reconnects with exponential backoff
 * - Exposes event handlers via options
 * - Returns connection status for UI indicator
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { ActivityEvent, ActivityEventType } from '../../components/guardLocation/ActivityFeed';

// ============================================
// Constants
// ============================================

/** Socket.IO namespace for dispatch events */
const DISPATCH_NAMESPACE = '/dispatch';

/** Initial reconnect delay in ms */
const RECONNECT_DELAY = 3000;

/** Maximum reconnect attempts before giving up */
const MAX_RECONNECT_ATTEMPTS = 10;

// ============================================
// Types
// ============================================

/** Patrol started event from server */
export interface PatrolStartedEvent {
  eventId: string;
  guardId: number;
  guardName: string;
  patrolId: number;
  siteName: string;
  timestamp: string;
}

/** Patrol completed event from server */
export interface PatrolCompletedEvent {
  eventId: string;
  guardId: number;
  guardName: string;
  patrolId: number;
  siteName: string;
  timestamp: string;
  checkpointsCompleted: number;
  checkpointsTotal: number;
}

/** Checkpoint completed event from server */
export interface CheckpointEvent {
  eventId: string;
  guardId: number;
  guardName: string;
  patrolId: number;
  checkpointName: string;
  checkpointsCompleted: number;
  checkpointsTotal: number;
  timestamp: string;
}

/** Location updated event from server */
export interface LocationUpdateEvent {
  guardId: number;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  batteryPercent: number | null;
  timestamp: string;
}

/** Alert created event from server */
export interface AlertEvent {
  eventId: string;
  alertId: string;
  type: 'no_activity' | 'left_site' | 'low_battery' | 'incomplete_patrol' | 'stale_patrol';
  severity: 'warning' | 'critical';
  guardId: number;
  guardName: string;
  message: string;
  details: {
    minutesSinceActivity?: number;
    batteryPercent?: number;
    siteName?: string;
    patrolId?: number;
  };
  timestamp: string;
}

/** Guard arrival event from server */
export interface GuardArrivedEvent {
  eventId: string;
  guardId: number;
  guardName: string;
  siteName: string;
  timestamp: string;
}

/** VMS incident alert event from server */
export interface VmsIncidentAlertEvent {
  incidentId: number;
  title: string;
  incidentType: string;
  severity: 'warning' | 'critical';
  cameraId: number | null;
  cameraName: string;
  siteId: number;
  siteName: string;
  thumbnailUrl: string | null;
  createdAt: string;
}

/** Guard departure event from server */
export interface GuardDepartedEvent {
  eventId: string;
  guardId: number;
  guardName: string;
  siteName: string;
  patrolId?: number;
  timestamp: string;
}

/** Alert dismissed by another dispatcher */
export interface AlertDismissedEvent {
  alertId: number;
  dismissedBy: number;
  timestamp: string;
}

/** Alert auto-resolved by background detection service */
export interface AlertResolvedEvent {
  alertId: number;
  alertType: string;
  contactId: number;
  guardName: string;
}

/** VMS incident status changed event from server */
export interface VmsIncidentStatusChangedEvent {
  incidentId: number;
  newStatus: string;
  previousStatus: string;
  updatedBy?: string;
  patrolId?: number;
  guardName?: string;
}

/** Options for useDispatchSocket hook */
export interface UseDispatchSocketOptions {
  /** Called when a patrol is started */
  onPatrolStarted?: (event: PatrolStartedEvent) => void;
  /** Called when a patrol is completed */
  onPatrolCompleted?: (event: PatrolCompletedEvent) => void;
  /** Called when a checkpoint is completed */
  onCheckpointCompleted?: (event: CheckpointEvent) => void;
  /** Called when a guard's location is updated */
  onLocationUpdated?: (event: LocationUpdateEvent) => void;
  /** Called when an alert is created */
  onAlertCreated?: (event: AlertEvent) => void;
  /** Called when a guard arrives at a site */
  onGuardArrived?: (event: GuardArrivedEvent) => void;
  /** Called when a guard departs from a site */
  onGuardDeparted?: (event: GuardDepartedEvent) => void;
  /** Called when a VMS incident alert is received */
  onVmsIncidentAlert?: (event: VmsIncidentAlertEvent) => void;
  /** Called when another dispatcher dismisses an alert */
  onAlertDismissed?: (event: AlertDismissedEvent) => void;
  /** Called when an alert is auto-resolved by background service */
  onAlertResolved?: (event: AlertResolvedEvent) => void;
  /** Called when a VMS incident status changes (dispatched, resolved, dismissed, etc.) */
  onVmsIncidentStatusChanged?: (event: VmsIncidentStatusChangedEvent) => void;
  /** Whether to enable the socket connection (default: true) */
  enabled?: boolean;
}

/** Return value from useDispatchSocket hook */
export interface UseDispatchSocketReturn {
  /** Whether the socket is currently connected */
  isConnected: boolean;
  /** Connection error, if any */
  connectionError: Error | null;
  /** Manual reconnect function */
  reconnect: () => void;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Constructs the Socket.IO URL following the same pattern as API URLs.
 * In production, uses the current host. In development, checks for IIS proxy.
 */
function getSocketUrl(): string {
  if (process.env.NODE_ENV === 'production') {
    return ''; // Same origin
  }

  // In development, check if we're running through IIS (port 8000)
  if (window.location.port === '8000') {
    return ''; // Same origin through IIS
  }

  // Direct React dev server - connect directly to backend
  return 'http://localhost:10000';
}

/**
 * Maps server events to ActivityEvent format for the feed
 */
export function mapToActivityEvent(
  type: ActivityEventType,
  event: PatrolStartedEvent | PatrolCompletedEvent | CheckpointEvent | GuardArrivedEvent | GuardDepartedEvent | AlertEvent
): ActivityEvent {
  const base = {
    eventId: event.eventId,
    type,
    guardId: event.guardId,
    guardName: event.guardName,
    timestamp: event.timestamp,
    details: {} as ActivityEvent['details'],
  };

  switch (type) {
    case 'patrol_started':
    case 'patrol_completed': {
      const patrolEvent = event as PatrolStartedEvent | PatrolCompletedEvent;
      base.details = {
        patrolId: patrolEvent.patrolId,
        siteName: patrolEvent.siteName,
      };
      if (type === 'patrol_completed') {
        const completedEvent = event as PatrolCompletedEvent;
        base.details.progress = `${completedEvent.checkpointsCompleted}/${completedEvent.checkpointsTotal}`;
      }
      break;
    }
    case 'checkpoint_completed': {
      const checkpointEvent = event as CheckpointEvent;
      base.details = {
        patrolId: checkpointEvent.patrolId,
        checkpointName: checkpointEvent.checkpointName,
        progress: `${checkpointEvent.checkpointsCompleted}/${checkpointEvent.checkpointsTotal}`,
      };
      break;
    }
    case 'guard_arrived':
    case 'guard_departed': {
      const geofenceEvent = event as GuardArrivedEvent | GuardDepartedEvent;
      base.details = {
        siteName: geofenceEvent.siteName,
      };
      break;
    }
    case 'alert': {
      const alertEvent = event as AlertEvent;
      base.details = {
        alertType: alertEvent.type,
        alertMessage: alertEvent.message,
        ...alertEvent.details,
      };
      break;
    }
  }

  return base;
}

// ============================================
// Hook Implementation
// ============================================

/**
 * Hook to manage Socket.IO connection to /dispatch namespace
 *
 * @param options - Event handlers and configuration
 * @returns Connection status and control functions
 *
 * @example
 * ```tsx
 * const { isConnected, connectionError } = useDispatchSocket({
 *   onPatrolStarted: (event) => {
 *     console.log('Patrol started:', event);
 *     // Update activity feed, guard status, etc.
 *   },
 *   onAlertCreated: (event) => {
 *     toast.warning(event.message);
 *   },
 * });
 * ```
 */
export function useDispatchSocket(
  options: UseDispatchSocketOptions = {}
): UseDispatchSocketReturn {
  const {
    onPatrolStarted,
    onPatrolCompleted,
    onCheckpointCompleted,
    onLocationUpdated,
    onAlertCreated,
    onGuardArrived,
    onGuardDeparted,
    onVmsIncidentAlert,
    onAlertDismissed,
    onAlertResolved,
    onVmsIncidentStatusChanged,
    enabled = true,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<Error | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Store callbacks in refs to avoid reconnecting when they change
  const callbacksRef = useRef({
    onPatrolStarted,
    onPatrolCompleted,
    onCheckpointCompleted,
    onLocationUpdated,
    onAlertCreated,
    onGuardArrived,
    onGuardDeparted,
    onVmsIncidentAlert,
    onAlertDismissed,
    onAlertResolved,
    onVmsIncidentStatusChanged,
  });

  // Update refs when callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onPatrolStarted,
      onPatrolCompleted,
      onCheckpointCompleted,
      onLocationUpdated,
      onAlertCreated,
      onGuardArrived,
      onGuardDeparted,
      onVmsIncidentAlert,
      onAlertDismissed,
      onAlertResolved,
      onVmsIncidentStatusChanged,
    };
  }, [
    onPatrolStarted,
    onPatrolCompleted,
    onCheckpointCompleted,
    onLocationUpdated,
    onAlertCreated,
    onGuardArrived,
    onGuardDeparted,
    onVmsIncidentAlert,
    onAlertDismissed,
    onAlertResolved,
    onVmsIncidentStatusChanged,
  ]);

  // Connect function
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return;
    }

    const socketUrl = getSocketUrl();

    // Decision: Using cookie auth (withCredentials) per existing codebase pattern
    // The session cookie provides authentication to the backend
    const socket = io(`${socketUrl}${DISPATCH_NAMESPACE}`, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: false, // We handle reconnection manually for more control
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      console.log('[Dispatch] Connected to server');
      setIsConnected(true);
      setConnectionError(null);
      reconnectAttemptsRef.current = 0;
    });

    socket.on('disconnect', (reason) => {
      console.log('[Dispatch] Disconnected:', reason);
      setIsConnected(false);

      // Auto-reconnect unless intentionally disconnected
      if (reason !== 'io client disconnect' && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current++;
        // Exponential backoff: 3s, 6s, 12s, 24s... up to 48s
        const delay = Math.min(RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1), 48000);
        console.log(`[Dispatch] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    });

    socket.on('connect_error', (err) => {
      console.error('[Dispatch] Connection error:', err);
      setConnectionError(new Error(err.message || 'Failed to connect to dispatch service'));
      setIsConnected(false);
    });

    // Patrol events
    socket.on('patrol:started', (data: PatrolStartedEvent) => {
      console.log('[Dispatch] Patrol started:', data);
      callbacksRef.current.onPatrolStarted?.(data);
    });

    socket.on('patrol:completed', (data: PatrolCompletedEvent) => {
      console.log('[Dispatch] Patrol completed:', data);
      callbacksRef.current.onPatrolCompleted?.(data);
    });

    // Checkpoint event
    socket.on('checkpoint:completed', (data: CheckpointEvent) => {
      console.log('[Dispatch] Checkpoint completed:', data);
      callbacksRef.current.onCheckpointCompleted?.(data);
    });

    // Location update
    socket.on('location:updated', (data: LocationUpdateEvent) => {
      // Don't log location updates to avoid console spam
      callbacksRef.current.onLocationUpdated?.(data);
    });

    // Alert created
    socket.on('alert:created', (data: AlertEvent) => {
      console.log('[Dispatch] Alert created:', data);
      callbacksRef.current.onAlertCreated?.(data);
    });

    // Geofence events
    socket.on('guard:arrived', (data: GuardArrivedEvent) => {
      console.log('[Dispatch] Guard arrived:', data);
      callbacksRef.current.onGuardArrived?.(data);
    });

    socket.on('guard:departed', (data: GuardDepartedEvent) => {
      console.log('[Dispatch] Guard departed:', data);
      callbacksRef.current.onGuardDeparted?.(data);
    });

    // VMS incident alert
    socket.on('vms:incident-alert', (data: VmsIncidentAlertEvent) => {
      console.log('[Dispatch] VMS incident alert:', data);
      callbacksRef.current.onVmsIncidentAlert?.(data);
    });

    // Alert dismissed by another dispatcher
    socket.on('alert:dismissed', (data: AlertDismissedEvent) => {
      console.log('[Dispatch] Alert dismissed:', data);
      callbacksRef.current.onAlertDismissed?.(data);
    });

    // Alert auto-resolved by background service
    socket.on('alert:resolved', (data: AlertResolvedEvent) => {
      console.log('[Dispatch] Alert resolved:', data);
      callbacksRef.current.onAlertResolved?.(data);
    });

    // VMS incident status changed (dispatched, resolved, dismissed, etc.)
    socket.on('vms:incident-status-changed', (data: VmsIncidentStatusChangedEvent) => {
      console.log('[Dispatch] VMS incident status changed:', data);
      callbacksRef.current.onVmsIncidentStatusChanged?.(data);
    });

    // Generic error handler
    socket.on('error', (error: { message: string }) => {
      console.error('[Dispatch] Error:', error.message);
      setConnectionError(new Error(error.message));
    });
  }, []);

  // Disconnect function
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0; // Reset attempts
    disconnect();
    setTimeout(connect, 100);
  }, [connect, disconnect]);

  // Effect to manage connection lifecycle
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    isConnected,
    connectionError,
    reconnect,
  };
}

export default useDispatchSocket;
